import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Client } from "@xmpp/client"
import { DriverContext } from "@/contexts/DriverContext"
import { XmppContext } from "@/stream/xmppClient"
import i18n from "@/lib/i18n/i18n"
import { retrieveDriverRoute } from "@/api/xeps/Routes"
import { useRouteTolls } from "@/hooks/useRouteTolls"
import { Location } from "@/reducers/locationsSlice"
import {
  isStopPending,
  Route2g,
  RouteStop,
  upsertRoute2g,
} from "@/reducers/routes2gSlice"
import { useAppDispatch } from "@/reducers/hooks"
import {
  indexOfStopKey,
  newStop,
  reorderStops,
  withStopAction,
  withStopAvoidTolls,
  withStopLocation,
} from "@/lib/routeStopEdits"

const locationFields = [
  "formattedAddress",
  "lat",
  "lon",
  "companyName",
  "street",
  "locality",
  "countryCode",
  "postalCode",
] as const

const includesEdit = (
  before: RouteStop[],
  saved: RouteStop[],
  received: RouteStop[],
) =>
  saved.length === received.length &&
  saved.every((stop, index) => {
    const incoming = received[index]
    if (stop.id && incoming.id !== stop.id) return false
    const previous = stop.id
      ? before.find((candidate) => candidate.id === stop.id)
      : before[index]
    if (!previous) {
      return !incoming.id || !before.some((item) => item.id === incoming.id)
    }
    if (previous.action !== stop.action && incoming.action !== stop.action) {
      return false
    }
    const changedFields = locationFields.filter(
      (field) => previous.location[field] !== stop.location[field],
    )
    return (
      changedFields.length === 0 ||
      changedFields.some(
        (field) => previous.location[field] !== incoming.location[field],
      )
    )
  })

/** An edit to a stop already on the route, waiting for the backend. */
export type PendingRowEdit =
  | { kind: "location"; address: string }
  | { kind: "remove" }

type PendingStopEdit =
  | { kind: "add"; key: string; address: string }
  | ({ key: string } & PendingRowEdit)

export type PendingStopAdd = { key: string; address: string }

const pickedAddress = (location: Location): string =>
  location.formattedAddress ??
  location.companyName ??
  `${location.lat}, ${location.lon}`

const confirmedRoute = async (
  client: Client,
  routeJid: string,
  editorJid: string,
  before: RouteStop[],
  saved: RouteStop[],
) => {
  const deadline = Date.now() + 8000
  while (true) {
    try {
      const route = await retrieveDriverRoute(client, routeJid)
      if (
        route?.lastUpdatedBy === editorJid &&
        includesEdit(before, saved, route.stops)
      ) {
        return route
      }
    } catch (error) {
      if (Date.now() >= deadline) throw error
    }
    if (Date.now() >= deadline) {
      throw new Error("The route update was not confirmed")
    }
    await new Promise((resolve) => setTimeout(resolve, 300))
  }
}

export const useDriverOverviewRoute = (
  routeJid: string | undefined,
  route: Route2g | undefined,
) => {
  const { upsertRoute2g: saveRoute } = useContext(DriverContext)
  const { client } = useContext(XmppContext)
  const dispatch = useAppDispatch()
  const [updating, setUpdating] = useState(false)
  const [pending, setPending] = useState<PendingStopEdit[]>([])
  const stops = useMemo(() => route?.stops ?? [], [route?.stops])
  const tolls = useRouteTolls(routeJid ?? "", stops.length > 0)
  const routeJidRef = useRef(routeJid)
  const generationRef = useRef(0)
  const writesRef = useRef(Promise.resolve())
  const writeCountRef = useRef(0)
  const addCountRef = useRef(0)

  useEffect(() => {
    if (routeJidRef.current === routeJid) return
    routeJidRef.current = routeJid
    generationRef.current += 1
    writesRef.current = Promise.resolve()
    writeCountRef.current = 0
    setUpdating(false)
    setPending([])
  }, [routeJid])

  const save = useCallback(
    (
      edit: (current: RouteStop[]) => RouteStop[],
      pendingEdit?: PendingStopEdit,
    ) => {
      if (!routeJid || !client?.jid) return Promise.resolve()
      const editorJid = client.jid.bare().toString()
      const generation = generationRef.current
      const settle = () =>
        setPending((current) => current.filter((item) => item !== pendingEdit))
      writeCountRef.current += 1
      setUpdating(true)
      if (pendingEdit) setPending((current) => [...current, pendingEdit])
      const write = writesRef.current.then(async () => {
        const latest = await retrieveDriverRoute(client, routeJid)
        const before = latest?.stops ?? []
        const next = edit(before)
        await saveRoute({ jid: routeJid, stops: next }, routeJid)
        const confirmed = await confirmedRoute(
          client,
          routeJid,
          editorJid,
          before,
          next,
        )
        if (generationRef.current === generation) {
          dispatch(upsertRoute2g({ route: confirmed }))
          settle()
        }
      })
      const reported = write.catch((error) => {
        if (generationRef.current === generation) {
          settle()
          const toaster = window.mapToaster ?? window.appToaster
          toaster?.show({
            message: i18n.t("chat.driverRoute.failedToUpdate"),
            intent: "danger",
            timeout: 3000,
          })
        }
        throw error
      })
      writesRef.current = reported.then(
        () => undefined,
        () => undefined,
      )
      const finish = () => {
        if (generationRef.current !== generation) return
        writeCountRef.current -= 1
        if (writeCountRef.current === 0) setUpdating(false)
      }
      void reported.then(finish, finish)
      return reported
    },
    [client, dispatch, routeJid, saveRoute],
  )

  const reorder = useCallback(
    (keys: string[]) => save((current) => reorderStops(current, keys)),
    [save],
  )
  const add = useCallback(
    (location: Location) => {
      addCountRef.current += 1
      return save(
        (current) => {
          const index = current.filter(isStopPending).length
          window.analytics.track("AddRouteStop", {
            source: "overview_bar",
            index,
            multi: index > 0,
          })
          return [...current, newStop(location)]
        },
        {
          kind: "add",
          key: `added-${addCountRef.current}`,
          address: pickedAddress(location),
        },
      )
    },
    [save],
  )
  const update = useCallback(
    (
      key: string,
      edit: (stop: RouteStop) => RouteStop,
      type: string,
      pendingEdit?: PendingStopEdit,
    ) =>
      save((current) => {
        const index = indexOfStopKey(current, key)
        const changed = [...current]
        const stop = changed[index]
        changed[index] = edit(stop)
        window.analytics.track("UpdateRouteStop", {
          id: stop.id,
          addedBy: stop.by,
          updateType: type,
        })
        return changed
      }, pendingEdit),
    [save],
  )
  const changeAction = useCallback(
    (key: string, action: string) =>
      update(key, (stop) => withStopAction(stop, action), "action"),
    [update],
  )
  const changeLocation = useCallback(
    (key: string, location: Location) =>
      update(key, (stop) => withStopLocation(stop, location), "location", {
        kind: "location",
        key,
        address: pickedAddress(location),
      }),
    [update],
  )

  const changeAvoidTolls = useCallback(
    (key: string, avoidTolls: boolean) =>
      update(key, (stop) => withStopAvoidTolls(stop, avoidTolls), "avoid"),
    [update],
  )
  const remove = useCallback(
    (key: string) =>
      save(
        (current) => {
          const index = indexOfStopKey(current, key)
          const stop = current[index]
          window.analytics.track("RemoveRouteStop", {
            id: stop.id,
            addedBy: stop.by,
          })
          return current.filter((_, i) => i !== index)
        },
        { kind: "remove", key },
      ),
    [save],
  )

  const pendingAdds = useMemo(
    () =>
      pending.flatMap((edit): PendingStopAdd[] =>
        edit.kind === "add" ? [{ key: edit.key, address: edit.address }] : [],
      ),
    [pending],
  )
  const pendingStops = useMemo(
    (): Record<string, PendingRowEdit> =>
      Object.fromEntries(
        pending.flatMap(({ key, ...edit }): [string, PendingRowEdit][] =>
          edit.kind === "add" ? [] : [[key, edit]],
        ),
      ),
    [pending],
  )

  return {
    updating,
    pendingAdds,
    pendingStops,
    reorder,
    add,
    changeAction,
    changeLocation,
    changeAvoidTolls,
    remove,
    tolls,
  }
}
